<script>
	import { onDestroy, onMount } from 'svelte';
	import Loader from './Loader.svelte';
	import TabView from './TabView.svelte';
	import TabPane from './TabPane.svelte';
	import GameTeamStatsTable from './GameTeamStatsTable.svelte';
	import GamePlayByPlay from './GamePlayByPlay.svelte';
	import GameTeamComparison from './GameTeamComparison.svelte';
	import GamePreview from './GamePreview.svelte';
	import {
		buildQuery,
		compUrl,
		getApiUrl,
		injectGlobalStyles,
		loadJson,
		normalizeCollection,
		normalizeText,
		teamUrl
	} from './shared.js';
	import { isAuthorized } from './auth.js';
	import { fetchBettings, getBetPercentages, makeBet, resolveBettingDate } from './betting.js';
	import AuthDialog from './AuthDialog.svelte';

	export let gameId = '';
	export let tag = 'mlbl';
	export let season = '';
	export let lang = 'ru';
	export let projectId = '';

	let host;
	let loading = false;
	let errorMessage = '';
	let game = null;
	let boxScore = null;
	let livePlays = [];
	let pollTimer = null;
	let flashTeam = 0;
	let flashTimeout = null;
	let prevScore1 = null;
	let prevScore2 = null;
	let pregame = null;

	// Состояние конкурса прогнозов
	let bettingsByProviderId = {};
	let authOpen = false;
	let pendingBet = null;
	let placingBet = false;
	let betError = '';

	$: teams = normalizeCollection(game?.teams);
	$: periods = parsePeriods(game?.game?.fullScore);
	$: referees = getRefereeList(teams);
	$: team1 = game?.team1;
	$: team2 = game?.team2;
	$: gameStatus = game?.game?.gameStatus || '';
	$: isOnline = gameStatus === 'Online' || gameStatus === 'Live';
	$: isScheduled = gameStatus === 'Scheduled';
	$: currentPlays = isOnline ? livePlays : normalizeCollection(game?.plays);
	$: startMap = buildStartMap(teams);
	$: onCourt = getOnCourtPlayers(boxScore, startMap);
	$: liveInfo = getLiveInfo(currentPlays, game?.live);
	$: score1 = getTeamScore(game, teams, 1, boxScore);
	$: score2 = getTeamScore(game, teams, 2, boxScore);
	$: gameDate = game?.game?.localDate || '';
	$: gameTime = game?.game?.localTime || '';
	$: arenaTitle = game?.arena?.name || game?.arena?.shortName || '';
	$: compTitle = game?.comp?.name || game?.comp?.abcName || game?.league?.name || game?.league?.abcName || '';
	$: activeTag = game?.comp?.tag || game?.league?.tag || tag;
	$: activeCompId = game?.comp?.id || game?.league?.id || '';
	$: gameTeam1 = teams.find((team) => team?.teamNumber === 1);
	$: gameTeam2 = teams.find((team) => team?.teamNumber === 2);
	$: team1Players = getTeamPlayers(gameTeam1);
	$: team2Players = getTeamPlayers(gameTeam2);
	$: statsExtraTeam1 = getStatsExtraTeam(game?.statsExtra, 1);
	$: statsExtraTeam2 = getStatsExtraTeam(game?.statsExtra, 2);
	$: team1Total = getTeamTotal(game, teams, boxScore, 1);
	$: team2Total = getTeamTotal(game, teams, boxScore, 2);

	$: betInfo = projectId ? (bettingsByProviderId[String(gameId ?? '')] || null) : null;
	$: betSide1 = betSideState(1, betInfo, gameStatus);
	$: betSide2 = betSideState(2, betInfo, gameStatus);

	function parsePeriods(fullScore) {
		if (typeof fullScore !== 'string' || !fullScore.trim()) {
			return [];
		}

		return fullScore
			.split(/[,;\s]+/)
			.map((part) => part.trim())
			.filter(Boolean)
			.map((part) => part.split(':').map((value) => Number(value)))
			.filter((pair) => pair.length === 2 && pair.every((value) => Number.isFinite(value)));
	}

	function getRefereeList(teamList) {
		const refereeTeam = teamList.find((team) => team?.teamNumber === 0);
		if (!refereeTeam) {
			return [];
		}

		return normalizeCollection(refereeTeam.starts)
			.filter((start) => start?.startRole === 'Referee')
			.map((start) => ({
				personId: start?.personId,
				lastName: start?.lastName,
				firstName: start?.firstName,
				secondName: start?.secondName,
				positionName: start?.positionName || start?.position || '',
				photo: start?.photo || '',
				age: start?.age,
				countryName: start?.countryName || ''
			}))
			.filter((referee) => [referee.lastName, referee.firstName, referee.secondName, referee.positionName].some(Boolean));
	}

	function getTeamScore(data, teamList, side, box) {
		const boxTeam = normalizeCollection(box?.teams).find((item) => item?.teamNumber === side);
		if (boxTeam?.total?.points !== null && boxTeam?.total?.points !== undefined) {
			return boxTeam.total.points;
		}

		const direct = data?.game?.[side === 1 ? 'score1' : 'score2'];
		if (direct !== null && direct !== undefined) {
			return direct;
		}

		const team = teamList.find((item) => item?.teamNumber === side);
		return team?.total?.points ?? null;
	}

	function getTeamId(team) {
		return team?.teamId || team?.id || '';
	}

	function getTeamTitle(team) {
		return team?.name || team?.shortName || team?.abcName || '—';
	}

	function getTeamInitials(team) {
		return (team?.shortName?.[0] || team?.abcName?.[0] || team?.name?.[0] || 'T').toUpperCase();
	}

	function getRefereeTitle(referee) {
		return [referee?.lastName, referee?.firstName, referee?.secondName].filter(Boolean).join(' ') || '—';
	}

	function getRefereeInitials(referee, fallback = 'R') {
		const first = referee?.firstName?.[0] || '';
		const last = referee?.lastName?.[0] || '';
		return `${first}${last}`.trim() || fallback;
	}

	function openTeam(team) {
		const id = getTeamId(team);
		if (!id) {
			return;
		}
		window.location.href = teamUrl(id, { tag: activeTag, compId: activeCompId });
	}

	function openComp() {
		const comp = game?.comp || game?.league;
		const compTag = comp?.tag || comp?.id;
		if (!compTag) {
			return;
		}
		window.location.href = compUrl(compTag, { season: comp?.season || season, lang });
	}

	function getTeamPlayers(team) {
		return normalizeCollection(team?.starts).filter((start) => start?.startRole === 'Player');
	}

	function getStatsExtraTeam(statsExtra, teamNumber) {
		return normalizeCollection(statsExtra?.teams).find((team) => team?.teamNumber === teamNumber) || null;
	}

	function getTeamTotal(data, teamList, box, side) {
		const boxTeam = normalizeCollection(box?.teams).find((team) => team?.teamNumber === side);
		if (boxTeam?.total) {
			return boxTeam.total;
		}
		const team = teamList.find((item) => item?.teamNumber === side);
		return team?.total || null;
	}

	function buildStartMap(teamList) {
		const map = {};
		for (const team of teamList) {
			for (const start of normalizeCollection(team?.starts)) {
				if (start?.startNum !== null && start?.startNum !== undefined) {
					map[start.startNum] = start;
				}
			}
		}
		return map;
	}

	function getPersonInitials(info, fallback = 'P') {
		const first = info?.firstName?.[0] || '';
		const last = info?.lastName?.[0] || '';
		return `${first}${last}`.trim() || fallback;
	}

	function getPointsLabel(points) {
		const value = Number(points) || 0;
		const mod10 = value % 10;
		const mod100 = value % 100;
		if (mod10 === 1 && mod100 !== 11) {
			return `${value} очко`;
		}
		if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
			return `${value} очка`;
		}
		return `${value} очков`;
	}

	function getOnCourtPlayers(box, map) {
		const result = { team1: [], team2: [] };
		for (const team of normalizeCollection(box?.teams)) {
			const teamNumber = team?.teamNumber;
			const players = normalizeCollection(team?.starts)
				.filter((stats) => stats?.isOnCourt === true)
				.map((stats) => {
					const info = map[stats?.startNum];
					return {
						photo: info?.photo || '',
						title: [info?.lastName, info?.firstName, info?.secondName].filter(Boolean).join(' '),
						number: info?.displayNumber || info?.playerNumber || '',
						initials: getPersonInitials(info),
						points: stats?.points ?? 0,
						assist: stats?.assist ?? 0,
						rebounds: (Number(stats?.defReb) || 0) + (Number(stats?.offReb) || 0),
						steal: stats?.steal ?? 0,
						foul: stats?.foul ?? 0,
						personId: info?.personId
					};
				});
			if (teamNumber === 1) {
				result.team1 = players;
			} else if (teamNumber === 2) {
				result.team2 = players;
			}
		}
		return result;
	}

	function getPeriodLabel(period) {
		const p = Number(period);
		if (p >= 1 && p <= 4) {
			return `${p} четверть`;
		}
		if (p > 4) {
			return 'Овертайм';
		}
		return '';
	}

	function formatCountdown(tenths) {
		const totalSeconds = Math.floor(Number(tenths) / 10);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
	}

	function getPeriodDurationTenths(live, period) {
		const periods = String(live?.periods || '')
			.split(',')
			.map((value) => Number(value))
			.filter((value) => Number.isFinite(value) && value > 0);
		const minutes = periods[(period ?? 1) - 1] ?? periods[periods.length - 1] ?? 10;
		return minutes * 60 * 10;
	}

	function getLiveInfo(playList, live) {
		const list = normalizeCollection(playList);
		let last = null;
		for (const play of list) {
			if (!last || (play?.num ?? 0) > (last?.num ?? 0)) {
				last = play;
			}
		}
		if (!last) {
			return { periodLabel: '', timeLeftLabel: '' };
		}
		const period = Number(last?.period);
		const duration = getPeriodDurationTenths(live, period);
		const sec = last?.sec;
		const left = sec !== null && sec !== undefined && duration ? Math.max(0, duration - Number(sec)) : null;
		return {
			periodLabel: getPeriodLabel(period),
			timeLeftLabel: left !== null ? formatCountdown(left) : ''
		};
	}

	function getBoxScorePoints(box, side) {
		const team = normalizeCollection(box?.teams).find((item) => item?.teamNumber === side);
		return team?.total?.points ?? null;
	}

	function triggerFlash(team) {
		flashTeam = team;
		if (flashTimeout) {
			clearTimeout(flashTimeout);
		}
		flashTimeout = setTimeout(() => {
			flashTeam = 0;
		}, 1200);
	}

	function stopPolling() {
		if (pollTimer) {
			clearInterval(pollTimer);
			pollTimer = null;
		}
	}

	function startPolling(status) {
		stopPolling();
		if (status === 'Online' || status === 'Live') {
			pollTimer = setInterval(pollOnline, 1000);
			pollOnline();
		} else if (status === 'Scheduled') {
			pollTimer = setInterval(pollScheduled, 5000);
		}
	}

	async function pollOnline() {
		try {
			const base = `${getApiUrl()}/ib/game/${encodeURIComponent(gameId)}`;
			const [box, playsData] = await Promise.all([
				loadJson(`${base}/box-score`),
				loadJson(`${base}/plays`)
			]);

			const newScore1 = getBoxScorePoints(box, 1);
			const newScore2 = getBoxScorePoints(box, 2);
			if (prevScore1 !== null && newScore1 !== null && newScore1 > prevScore1) {
				triggerFlash(1);
			} else if (prevScore2 !== null && newScore2 !== null && newScore2 > prevScore2) {
				triggerFlash(2);
			}
			prevScore1 = newScore1;
			prevScore2 = newScore2;

			boxScore = box;
			livePlays = normalizeCollection(playsData);
		} catch (error) {
			console.error('Poll online error:', error);
		}
	}

	async function pollScheduled() {
		try {
			const url = `${getApiUrl()}/ib/game/${encodeURIComponent(gameId)}/info${buildQuery({ tag, season, lang })}`;
			const fresh = await loadJson(url);
			game = fresh;
			const status = fresh?.game?.gameStatus;
			if (status === 'Online' || status === 'Live') {
				startPolling(status);
			} else if (status !== 'Scheduled') {
				stopPolling();
			}
		} catch (error) {
			console.error('Poll scheduled error:', error);
		}
	}

	async function loadGame() {
		if (!gameId) {
			errorMessage = 'Не указан идентификатор игры';
			game = null;
			return;
		}

		loading = true;
		errorMessage = '';
		try {
			const url = `${getApiUrl()}/ib/game/${encodeURIComponent(gameId)}/info${buildQuery({ tag, season, lang })}`;
			game = await loadJson(url);
			startPolling(game?.game?.gameStatus);
			loadPregame();
			loadBettings();
		} catch (error) {
			console.error('Load game info error:', error);
			errorMessage = 'Ошибка загрузки игры';
			game = null;
		} finally {
			loading = false;
		}
	}

	async function loadPregame() {
		try {
			const url = `${getApiUrl()}/ib/game/${encodeURIComponent(gameId)}/pregame${buildQuery({ tag, season, lang })}`;
			pregame = await loadJson(url);
		} catch (error) {
			console.error('Load pregame error:', error);
			pregame = null;
		}
	}

	// --- ЛОГИКА КОНКУРСА ПРОГНОЗОВ ---

	function getBetTeamName(team) {
		return team?.shortName || team?.name || team?.abcName || '';
	}

	function betSideState(side, info, status) {
		const pct = getBetPercentages(info);
		const finished = Boolean(info?.isResolved) || status === 'Result' || status === 'ResultConfirmed';
		const sidePct = side === 1 ? pct.p1 : pct.p2;
		const isYourBet = info?.yourBet === side;
		const result = info?.result;
		const youWon = finished && isYourBet && result !== null && result !== undefined && result === info?.yourBet;
		const youLost = finished && isYourBet && result !== null && result !== undefined && result !== info?.yourBet;
		return { sidePct, finished, isYourBet, youWon, youLost, total: pct.total };
	}

	async function loadBettings() {
		if (!projectId || !gameId) {
			bettingsByProviderId = {};
			return;
		}
		try {
			bettingsByProviderId = await fetchBettings([String(gameId)]);
		} catch (error) {
			console.error('Load bettings error:', error);
		}
	}

	async function handleBet(side) {
		if (!projectId || !gameId) return;
		betError = '';
		pendingBet = side;
		if (!isAuthorized()) {
			authOpen = true;
			return;
		}
		await placePendingBet();
	}

	async function handleAuthorized() {
		if (pendingBet !== null) {
			await placePendingBet();
		}
	}

	async function placePendingBet() {
		const side = pendingBet;
		pendingBet = null;
		if (side === null || !isAuthorized()) return;

		placingBet = true;
		try {
			await makeBet({
				projectId,
				providerId: String(gameId),
				bettingDate: resolveBettingDate(game?.game),
				result1Name: getBetTeamName(team1),
				result2Name: getBetTeamName(team2),
				bet: side
			});
			await loadBettings();
		} catch (error) {
			console.error('Make bet error:', error);
			betError = error.message || 'Не удалось сделать ставку';
		} finally {
			placingBet = false;
		}
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) {
			injectGlobalStyles(root);
		}

		loadGame();
	});

	onDestroy(() => {
		stopPolling();
		if (flashTimeout) {
			clearTimeout(flashTimeout);
		}
	});
</script>

<div class="widget-card" bind:this={host}>
	{#if loading}
		<div class="widget-card__status"><Loader size="64px" /></div>
	{:else if errorMessage}
		<div class="widget-card__status">{errorMessage}</div>
	{:else if game}
		<div class="scoreboard">
			<div class="scoreboard__top">
				{#if compTitle}
					<button type="button" class="scoreboard__comp" on:click={openComp}>{compTitle}</button>
				{/if}

				{#if gameDate || gameTime || arenaTitle}
					<div class="scoreboard__meta">
						{#if gameDate || gameTime}
							<span class="scoreboard__meta-item">{gameDate}{gameTime ? `, ${gameTime}` : ''}</span>
						{/if}
						{#if arenaTitle}
							{#if gameDate || gameTime}<span class="scoreboard__meta-sep">•</span>{/if}
							<span class="scoreboard__meta-item">{arenaTitle}</span>
						{/if}
					</div>
				{/if}

				{#if isOnline && (liveInfo.periodLabel || liveInfo.timeLeftLabel)}
					<div class="scoreboard__live">
						<span class="scoreboard__live-dot"></span>
						<span>{liveInfo.periodLabel}{liveInfo.timeLeftLabel ? ` • ${liveInfo.timeLeftLabel}` : ''}</span>
					</div>
				{/if}
			</div>

			<div class="scoreboard__line scoreboard__line--top">
				<div class="scoreboard__side scoreboard__side--left">
					<button type="button" class="scoreboard__team" on:click={() => openTeam(team1)}>
						{#if team1?.logo}
							<img class="scoreboard__logo" src={team1.logo} alt={getTeamTitle(team1)}>
						{:else}
							<div class="scoreboard__logo scoreboard__logo--placeholder">{getTeamInitials(team1)}</div>
						{/if}
					</button>
				</div>

				<div class="scoreboard__scorebox">
					<span class="scoreboard__score-value" class:scoreboard__score-value--flash={flashTeam === 1}>{score1 ?? '—'}</span>
					<span class="scoreboard__score-sep">:</span>
					<span class="scoreboard__score-value" class:scoreboard__score-value--flash={flashTeam === 2}>{score2 ?? '—'}</span>
				</div>

				<div class="scoreboard__side scoreboard__side--right">
					<button type="button" class="scoreboard__team" on:click={() => openTeam(team2)}>
						{#if team2?.logo}
							<img class="scoreboard__logo" src={team2.logo} alt={getTeamTitle(team2)}>
						{:else}
							<div class="scoreboard__logo scoreboard__logo--placeholder">{getTeamInitials(team2)}</div>
						{/if}
					</button>
				</div>
			</div>

			<div class="scoreboard__line scoreboard__line--names">
				<button type="button" class="scoreboard__team-name scoreboard__team-name--left" on:click={() => openTeam(team1)}>
					{getTeamTitle(team1)}
				</button>

				<div class="scoreboard__periods">
					{#if periods.length}
						{#each periods as period, index}
							<div class="scoreboard__period">
								<span class="scoreboard__period-num">{index + 1}</span>
								<span class="scoreboard__period-score">{period[0]}:{period[1]}</span>
							</div>
						{/each}
					{:else}
						<span class="scoreboard__period-empty">—</span>
					{/if}
				</div>

				<button type="button" class="scoreboard__team-name scoreboard__team-name--right" on:click={() => openTeam(team2)}>
					{getTeamTitle(team2)}
				</button>
			</div>

			{#if projectId && !isOnline}
				<div class="scoreboard__line scoreboard__line--betting">
					<div class="scoreboard__bet scoreboard__bet--left">
						{#if betSide1.finished}
							{#if betSide1.isYourBet}
								<span class="scoreboard__bet-descr">{betSide1.youWon ? 'Вы выиграли' : (betSide1.youLost ? 'Вы проиграли' : 'Ваша ставка')}</span>
							{/if}
							<span class="scoreboard__bet-value" class:--green={betSide1.youWon} class:--red={betSide1.youLost}>{betSide1.sidePct}%</span>
						{:else if betSide1.total > 0}
							{#if betSide1.isYourBet}<span class="scoreboard__bet-descr">Ваша ставка</span>{/if}
							<button type="button" class="scoreboard__bet-value scoreboard__bet-value--btn" class:--active={betSide1.isYourBet} disabled={placingBet} on:click={() => handleBet(1)}>{betSide1.sidePct}%</button>
						{:else}
							<button type="button" class="scoreboard__bet-btn" disabled={placingBet} on:click={() => handleBet(1)}>Прогноз</button>
						{/if}
					</div>

					<div class="scoreboard__bet-center">
						{#if betSide1.finished}
							<span class="scoreboard__bet-result">{score1 ?? '—'} : {score2 ?? '—'}</span>
						{:else}
							<span class="scoreboard__bet-label">Прогнозы</span>
						{/if}
					</div>

					<div class="scoreboard__bet scoreboard__bet--right">
						{#if betSide2.finished}
							{#if betSide2.isYourBet}
								<span class="scoreboard__bet-descr">{betSide2.youWon ? 'Вы выиграли' : (betSide2.youLost ? 'Вы проиграли' : 'Ваша ставка')}</span>
							{/if}
							<span class="scoreboard__bet-value" class:--green={betSide2.youWon} class:--red={betSide2.youLost}>{betSide2.sidePct}%</span>
						{:else if betSide2.total > 0}
							{#if betSide2.isYourBet}<span class="scoreboard__bet-descr">Ваша ставка</span>{/if}
							<button type="button" class="scoreboard__bet-value scoreboard__bet-value--btn" class:--active={betSide2.isYourBet} disabled={placingBet} on:click={() => handleBet(2)}>{betSide2.sidePct}%</button>
						{:else}
							<button type="button" class="scoreboard__bet-btn" disabled={placingBet} on:click={() => handleBet(2)}>Прогноз</button>
						{/if}
					</div>
				</div>
				{#if betError}<div class="scoreboard__bet-error" role="alert">{betError}</div>{/if}
			{/if}
		</div>

		{#if game?.game?.video}
			<div class="video-card">
				{@html game.game.video}
			</div>
		{/if}

		{#if isOnline}
			<div class="online-card">
				<div class="online-card__title">На площадке</div>
				<div class="online-card__grid">
					<div class="online-card__column">
						{#each onCourt.team1 as player}
							<div class="online-player">
								{#if player.photo}
									<img class="online-player__photo" src={player.photo} alt={player.title}>
								{:else}
									<div class="online-player__photo online-player__photo--placeholder">{player.initials}</div>
								{/if}
								<div class="online-player__body">
									<div class="online-player__name"><small>#{player.number}</small> {player.title || '—'}</div>
									<div class="online-player__stats">
										<span>{getPointsLabel(player.points)}</span>
										{#if player.assist}<span>{player.assist} гп</span>{/if}
										{#if player.rebounds}<span>{player.rebounds} пб</span>{/if}
										{#if player.steal}<span>{player.steal} пх</span>{/if}
										{#if player.foul}<span>{player.foul} ф</span>{/if}
									</div>
								</div>
							</div>
						{/each}
					</div>
					<div class="online-card__column">
						{#each onCourt.team2 as player}
							<div class="online-player">
								{#if player.photo}
									<img class="online-player__photo" src={player.photo} alt={player.title}>
								{:else}
									<div class="online-player__photo online-player__photo--placeholder">{player.initials}</div>
								{/if}
								<div class="online-player__body">
									<div class="online-player__name"><small>#{player.number}</small> {player.title || '—'}</div>
									<div class="online-player__stats">
										<span>{getPointsLabel(player.points)}</span>
										{#if player.assist}<span>{player.assist} гп</span>{/if}
										{#if player.rebounds}<span>{player.rebounds} пб</span>{/if}
										{#if player.steal}<span>{player.steal} пх</span>{/if}
										{#if player.foul}<span>{player.foul} ф</span>{/if}
									</div>
								</div>
							</div>
						{/each}
					</div>
				</div>
			</div>
		{/if}

		{#if !isScheduled}
		<TabView>
			<TabPane title="Протокол">
				<div class="widget-tabs__body">
					<GameTeamStatsTable
						team={team1}
						onTeamClick={() => openTeam(team1)}
						players={team1Players}
						total={gameTeam1?.total}
						startTotal={gameTeam1?.startTotal}
						benchTotal={gameTeam1?.benchTotal}
						statsExtra={statsExtraTeam1}
						tag={activeTag}
						compId={activeCompId}
					/>

					<GameTeamStatsTable
						team={team2}
						onTeamClick={() => openTeam(team2)}
						players={team2Players}
						total={gameTeam2?.total}
						startTotal={gameTeam2?.startTotal}
						benchTotal={gameTeam2?.benchTotal}
						statsExtra={statsExtraTeam2}
						tag={activeTag}
						compId={activeCompId}
					/>

					{#if game?.statsExtra}
						<div class="protocol-general">
							<h4 class="protocol-general__title">Дополнительно</h4>
							<div class="protocol-general__metrics">
								<div class="protocol-general__metric">
									<span>Ничьи</span>
									<strong>{normalizeText(game.statsExtra.draws)}</strong>
								</div>
								<div class="protocol-general__metric">
									<span>Смены лидера</span>
									<strong>{normalizeText(game.statsExtra.leaderChanges)}</strong>
								</div>
							</div>
						</div>
					{/if}
				</div>
			</TabPane>

			<TabPane title="Статистика команд">
				<div class="widget-tabs__body">
					<GameTeamComparison team1={team1Total} team2={team2Total} />
				</div>
			</TabPane>

			<TabPane title="Ход игры">
				<div class="widget-tabs__body">
					<GamePlayByPlay
						plays={currentPlays}
						teams={teams}
						team1={team1}
						team2={team2}
						tag={activeTag}
						compId={activeCompId}
					/>
				</div>
			</TabPane>

			<TabPane title="Судьи">
				<div class="widget-tabs__body">
					{#if referees.length}
						<div class="person-grid">
							{#each referees as referee}
								<div class="person-card">
									<div class="person-card__head">
										{#if referee.photo}
											<img class="person-card__photo" src={referee.photo} alt={getRefereeTitle(referee)}>
										{:else}
											<div class="person-card__placeholder">{getRefereeInitials(referee, 'R')}</div>
										{/if}
										<div>
											<strong>{getRefereeTitle(referee)}</strong>
											<p>{normalizeText(referee.positionName)}</p>
										</div>
									</div>
									<div class="person-card__meta">
										{#if referee.age}<span>{referee.age} лет</span>{/if}
										{#if referee.countryName}<span>{referee.countryName}</span>{/if}
									</div>
								</div>
							{/each}
						</div>
					{:else}
						<div class="widget-card__status --small">Судьи не найдены</div>
					{/if}
				</div>
			</TabPane>
		</TabView>
		{:else}
		<div class="scheduled-wait">
			<span class="scheduled-wait__dot"></span>
			<span>Ожидание начала матча</span>
		</div>
		<GamePreview pregame={pregame} team1={team1} team2={team2} tag={activeTag} compId={activeCompId} />
		{/if}
	{/if}
</div>

<AuthDialog bind:open={authOpen} intro="Войдите по номеру телефона, чтобы сделать прогноз на матч." on:authorized={handleAuthorized} on:close={() => { pendingBet = null; }} />

<style>
	:host { display: block; }
	.widget-card {
		border: 1px solid #d7dce2;
		border-radius: 16px;
		padding: 18px;
		background: #fff;
		box-shadow: 0 6px 24px rgba(8, 22, 47, 0.05);
	}
	.widget-card__status {
		display: flex;
		justify-content: center;
		align-items: center;
		min-height: 160px;
		color: #53657b;
		text-align: center;
	}
	.widget-card__status.--small {
		min-height: 88px;
	}
	.widget-tabs__body {
		padding: 4px 0 0;
	}

	.scoreboard {
		display: flex;
		flex-direction: column;
		gap: 12px;
		padding: 20px 12px;
		margin-bottom: 8px;
		border-radius: 14px;
		background: linear-gradient(180deg, #f7f9fc 0%, #eef2f8 100%);
	}

	.scoreboard__top {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
	}

	.scoreboard__comp {
		border: 0;
		background: transparent;
		padding: 0;
		font: inherit;
		font-size: 14px;
		font-weight: 700;
		color: #00244d;
		text-transform: uppercase;
		letter-spacing: 0.02em;
		cursor: pointer;
		transition: color 0.2s ease;
	}
	.scoreboard__comp:hover {
		color: #0b4fa8;
		text-decoration: underline;
	}

	.scoreboard__line {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		align-items: center;
		gap: 16px;
	}

	.scoreboard__side {
		display: flex;
		align-items: center;
	}
	.scoreboard__side--left { justify-content: flex-start; }
	.scoreboard__side--right { justify-content: flex-end; }

	.scoreboard__team {
		display: flex;
		align-items: center;
		border: 0;
		background: transparent;
		padding: 0;
		cursor: pointer;
		border-radius: 14px;
	}

	.scoreboard__logo {
		width: 72px;
		height: 72px;
		border-radius: 14px;
		object-fit: contain;
		background: #fff;
		border: 1px solid #e3e8ef;
		transition: border-color 0.2s ease;
	}
	.scoreboard__logo--placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 26px;
		font-weight: 700;
		color: #00244d;
	}
	.scoreboard__team:hover .scoreboard__logo,
	.scoreboard__team:hover .scoreboard__logo--placeholder {
		border-color: #b3c2d6;
	}

	.scoreboard__scorebox {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		padding: 10px 22px;
		border-radius: 14px;
		background: #fff;
		border: 1px solid #e3e8ef;
		box-shadow: 0 2px 10px rgba(8, 22, 47, 0.06);
	}
	.scoreboard__score-value {
		min-width: 40px;
		text-align: center;
		font-size: 40px;
		font-weight: 800;
		line-height: 1;
		color: #122033;
	}
	.scoreboard__score-sep {
		font-size: 30px;
		font-weight: 700;
		color: #6b7a90;
	}

	.scoreboard__team-name {
		border: 0;
		background: transparent;
		padding: 0;
		font: inherit;
		font-size: 17px;
		font-weight: 700;
		color: #122033;
		line-height: 1.25;
		cursor: pointer;
		transition: color 0.2s ease;
	}
	.scoreboard__team-name:hover {
		color: #0b4fa8;
	}
	.scoreboard__team-name--left { text-align: left; }
	.scoreboard__team-name--right { text-align: right; }

	.scoreboard__periods {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}
	.scoreboard__period {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 2px;
		min-width: 44px;
		padding: 6px 8px;
		border-radius: 8px;
		background: #fff;
		border: 1px solid #e3e8ef;
	}
	.scoreboard__period-num {
		font-size: 11px;
		font-weight: 700;
		color: #6b7a90;
		text-transform: uppercase;
	}
	.scoreboard__period-score {
		font-size: 15px;
		font-weight: 700;
		color: #122033;
	}
	.scoreboard__period-empty {
		font-size: 15px;
		color: #6b7a90;
	}

	.scoreboard__meta {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		flex-wrap: wrap;
		font-size: 13px;
		color: #53657b;
	}
	.scoreboard__meta-item { font-weight: 600; }
	.scoreboard__meta-sep { color: #d7dce2; }

	.person-grid {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 12px;
	}
	.person-card {
		border: 1px solid #d7dce2;
		border-radius: 12px;
		padding: 12px;
		background: #fff;
	}
	.person-card__head {
		display: flex;
		gap: 12px;
		align-items: center;
	}
	.person-card__head strong {
		display: block;
		font-size: 15px;
		color: #122033;
	}
	.person-card__head p {
		margin: 4px 0 0;
		font-size: 13px;
		color: #53657b;
	}
	.person-card__photo,
	.person-card__placeholder {
		width: 72px;
		height: 72px;
		border-radius: 7px;
		object-fit: cover;
		background: #edf2f7;
		flex: 0 0 72px;
	}
	.person-card__placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 18px;
		font-weight: 700;
		color: #00244d;
	}
	.person-card__meta {
		display: flex;
		gap: 10px;
		flex-wrap: wrap;
		margin-top: 10px;
		font-size: 12px;
		color: #53657b;
	}

	.protocol-general {
		margin-top: 4px;
	}
	.protocol-general__title {
		margin: 0 0 12px;
		font-size: 18px;
		font-weight: 700;
		color: #122033;
	}
	.protocol-general__metrics {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 10px;
	}
	.protocol-general__metric {
		padding: 10px 12px;
		border-radius: 10px;
		background: #f7f9fc;
		border: 1px solid #eef1f6;
	}
	.protocol-general__metric span {
		display: block;
		margin-bottom: 4px;
		font-size: 11px;
		text-transform: uppercase;
		color: #6b7a90;
	}
	.protocol-general__metric strong {
		font-size: 15px;
		color: #122033;
	}

	.scoreboard__live {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		font-size: 13px;
		font-weight: 700;
		color: #d33a3a;
	}
	.scoreboard__live-dot {
		width: 8px;
		height: 8px;
		border-radius: 999px;
		background: #d33a3a;
		animation: live-pulse 1.4s ease-in-out infinite;
	}
	@keyframes live-pulse {
		0%, 100% { opacity: 1; }
		50% { opacity: 0.3; }
	}

	.scoreboard__score-value--flash {
		animation: score-flash 1s ease-out;
	}
	@keyframes score-flash {
		0% { color: #16a34a; transform: scale(1.3); }
		100% { color: #122033; transform: scale(1); }
	}

	.scoreboard__line--betting {
		grid-template-columns: 1fr auto 1fr;
		gap: 12px;
		margin-top: 4px;
		padding-top: 14px;
		border-top: 1px dashed #d7dce2;
	}

	.scoreboard__bet {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 5px;
		min-width: 0;
	}
	.scoreboard__bet--left { align-items: flex-start; }
	.scoreboard__bet--right { align-items: flex-end; }

	.scoreboard__bet-descr {
		font-size: 11px;
		color: #6b7a90;
	}

	.scoreboard__bet-value,
	.scoreboard__bet-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 64px;
		min-height: 30px;
		padding: 5px 14px;
		border: 1px solid #e3e8ef;
		border-radius: 8px;
		background: #fff;
		color: #122033;
		font-family: inherit;
		font-size: 14px;
		font-weight: 700;
		line-height: 1;
	}

	.scoreboard__bet-value--btn {
		cursor: pointer;
		transition: border-color 150ms ease, background-color 150ms ease;
	}
	.scoreboard__bet-value--btn:hover:not(:disabled) {
		border-color: #b3c2d6;
		background: #f5f8fc;
	}
	.scoreboard__bet-value--btn:disabled {
		cursor: default;
		opacity: 0.6;
	}
	.scoreboard__bet-value--btn.--active {
		border-color: #12ab61;
		background: #12ab61;
		color: #fff;
	}
	.scoreboard__bet-value.--green {
		border-color: #12ab61;
		background: #12ab61;
		color: #fff;
	}
	.scoreboard__bet-value.--red {
		border-color: #ca2319;
		background: #ca2319;
		color: #fff;
	}

	.scoreboard__bet-btn {
		cursor: pointer;
		border-color: #00244d;
		background: #16202c;
		color: #fff;
		transition: background-color 150ms ease, border-color 150ms ease;
	}
	.scoreboard__bet-btn:hover:not(:disabled) {
		background: #123e6d;
		border-color: #123e6d;
	}
	.scoreboard__bet-btn:disabled {
		cursor: default;
		opacity: 0.6;
	}

	.scoreboard__bet-center {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 4px;
		min-width: 72px;
	}
	.scoreboard__bet-label {
		font-size: 11px;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		color: #6b7a90;
	}
	.scoreboard__bet-result {
		font-size: 22px;
		font-weight: 800;
		color: #122033;
		line-height: 1;
	}
	.scoreboard__bet-error {
		margin-top: 8px;
		text-align: center;
		color: #a52f2f;
		font-size: 13px;
	}

	.online-card {
		margin: 8px 0;
		padding: 14px;
		border-radius: 14px;
		background: #fff;
		border: 1px solid #e3e8ef;
		box-shadow: 0 2px 10px rgba(8, 22, 47, 0.05);
	}
	.online-card__title {
		margin: 0 0 12px;
		font-size: 14px;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		color: #00244d;
	}
	.online-card__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 16px;
	}
	.online-card__column {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	.online-player {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px;
		border-radius: 10px;
		background: #f7f9fc;
	}
	.online-player__photo {
		width: 42px;
		height: 42px;
		border-radius: 7px;
		object-fit: cover;
		background: #e6ecf4;
		flex: 0 0 42px;
	}
	.online-player__photo--placeholder {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		font-size: 14px;
		font-weight: 700;
		color: #00244d;
	}
	.online-player__body {
		min-width: 0;
	}
	.online-player__name {
		font-size: 13px;
		font-weight: 700;
		color: #122033;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.online-player__name small {
		opacity: 0.5;
	}
	.online-player__stats {
		display: flex;
		gap: 8px;
		margin-top: 3px;
		font-size: 11px;
		color: #6b7a90;
	}
	.online-player__stats b {
		color: #122033;
		font-weight: 700;
	}

	.video-card {
		margin: 8px 0;
	}
	.video-card :global(iframe) {
		display: block;
		width: 100%;
		aspect-ratio: 16 / 9;
		height: auto;
		border: 0;
		border-radius: 12px;
	}

	.scheduled-wait {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		min-height: 120px;
		color: #53657b;
		font-size: 14px;
		font-weight: 600;
	}
	.scheduled-wait__dot {
		width: 9px;
		height: 9px;
		border-radius: 999px;
		background: #e07800;
		animation: live-pulse 1.4s ease-in-out infinite;
	}

	@media (max-width: 1200px) {
		.person-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
	}
	@media (max-width: 900px) {
		.person-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
	}
	@media (max-width: 640px) {
		.person-grid { grid-template-columns: 1fr; }
		.scoreboard__logo { width: 56px; height: 56px; }
		.scoreboard__score-value { font-size: 30px; min-width: 32px; }
		.scoreboard__score-sep { font-size: 22px; }
		.scoreboard__team-name { font-size: 15px; }
		.scoreboard__period { min-width: 36px; padding: 4px 6px; }
		.scoreboard__period-score { font-size: 13px; }
		.online-card__grid { grid-template-columns: 1fr; }
	}
</style>
